import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { setActiveLocale } from './utils/activeLocale';

i18n
  // Load translations from backend
  .use(Backend)
  // Detect user language
  .use(LanguageDetector)
  // Pass i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18n
  .init({
    // Used when the detected language isn't one we ship, and for any key
    // missing from the active language's files.
    fallbackLng: 'en',
    // List of supported languages - only include these
    supportedLngs: ['en', 'de'],
    // NOTE: `lng` is deliberately NOT set. Passing it short-circuits the
    // LanguageDetector entirely, which is what previously made the user's
    // choice reset to German on every reload.
    // The browser reports regional codes ('en-US', 'de-DE'); resolve them to
    // the language folders we actually ship rather than requesting
    // `/locales/en-US/common.json`.
    load: 'languageOnly',
    // Debug in development
    debug: process.env.NODE_ENV === 'development',
    // localStorage holds the user's explicit choice; the browser locale is the
    // first-visit default. Nothing server-side reads this preference (static
    // SPA, separate API host, no `credentials: 'include'`), so a cookie would
    // travel on every request to no purpose and add an expiry to manage.
    detection: {
      order: ['localStorage', 'navigator'],
      // NOT i18next's default key `i18nextLng`. While `lng: 'de'` was set, every
      // page load ran `cacheUserLanguage('de')` (i18next's `setLng`), so that key
      // holds a hardcoded 'de' for every existing user. Reading it would pin them
      // all to German permanently — the very bug this fixes. A fresh key starts
      // empty, so those users fall through to `navigator` once and then cache
      // their real choice. The stale key is simply never read again.
      lookupLocalStorage: 'gchatLang',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    // Load namespaces
    ns: ['common', 'home', 'chat', 'advisory'],
    defaultNS: 'common',
    // Backend configuration for loading translation files
    backend: {
      // Path to load translations from
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      // Locale URLs are shared by releases. Revalidate even when an older
      // deployment allowed the browser to keep its response fresh for an hour.
      requestOptions: { cache: 'no-cache' },
      // Allow partial loading
      allowMultiLoading: false,
    },
  });

/**
 * Push the active language into `utils/activeLocale`, which `dateUtils`
 * reads when formatting timestamps. The dependency deliberately points this
 * way — i18n knows about the utils, the utils know nothing about i18next
 * (see `utils/activeLocale.ts` for why that matters to the type gate).
 */
setActiveLocale(i18n.language);
i18n.on('languageChanged', lng => setActiveLocale(lng));

export default i18n;
